/*
 * Yomiri Community Plugin Directory
 * Verified, legal, open-source community plugins ready for 1-click install.
 */

import type { InstalledPlugin, YomiriPluginManifest } from './PluginTypes.ts';

export interface CommunityPluginEntry {
  manifest: YomiriPluginManifest;
  url: string;
  category: 'tv' | 'movies' | 'manga' | 'anime' | 'sports';
  featured?: boolean;
  staticPayload?: InstalledPlugin['staticPayload'];
}

export const COMMUNITY_PLUGINS: CommunityPluginEntry[] = [
  {
    url: 'yomiri://plugin/tv.argentina.open',
    category: 'tv',
    featured: true,
    manifest: {
      id: 'tv.argentina.open',
      name: 'TV Abierta Argentina',
      version: '1.2.0',
      description: 'Canales oficiales de aire, noticias y deportes de Argentina transmitidos en vivo por YouTube y señales públicas autorizadas.',
      icon: 'https://images.unsplash.com/photo-1593784991095-a205069470b6?w=128&auto=format&fit=crop&q=80',
      author: 'Comunidad Abierta Yomiri',
      types: ['tv'],
      resources: ['catalog', 'meta', 'stream'],
      catalogs: [
        {
          type: 'tv',
          id: 'canales-aire',
          name: 'Canales de Aire & Noticias',
          extra: [{ name: 'genre', options: ['Aire', 'Noticias', 'Educativo', 'Deportes'] }],
        },
      ],
      behaviorHints: {
        defaultSection: 'tv',
        sectionTitle: 'TV en Vivo',
        sectionIcon: 'LiveTv',
        staticData: true,
      },
    },
    staticPayload: {
      catalogs: {
        'tv:canales-aire': [
          {
            id: 'c5n',
            type: 'tv',
            name: 'C5N en Vivo',
            poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/C5N_logo_2021.svg/512px-C5N_logo_2021.svg.png',
            genres: ['Noticias'],
            description: 'Transmisión 24/7 de noticias oficiales de C5N en directo.',
            isLive: true,
            brandBg: '#002B49',
          },
          {
            id: 'tn',
            type: 'tv',
            name: 'TN Todo Noticias',
            poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c2/TN_Logo_2016.svg/512px-TN_Logo_2016.svg.png',
            genres: ['Noticias'],
            description: 'Canal de noticias Todo Noticias 24hs en directo.',
            isLive: true,
            brandBg: '#0F1D38',
          },
          {
            id: 'tv-publica',
            type: 'tv',
            name: 'TV Pública',
            poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Televisi%C3%B3n_P%C3%BAblica_Argentina_%282021%29.svg/512px-Televisi%C3%B3n_P%C3%BAblica_Argentina_%282021%29.svg.png',
            genres: ['Aire', 'Educativo'],
            description: 'Señal pública abierta nacional de la República Argentina.',
            isLive: true,
            brandBg: '#1C2833',
          },
          {
            id: 'encuentro',
            type: 'tv',
            name: 'Canal Encuentro',
            poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Canal_Encuentro_logo_2020.svg/512px-Canal_Encuentro_logo_2020.svg.png',
            genres: ['Educativo'],
            description: 'Canal educativo y cultural del Ministerio de Educación.',
            isLive: true,
            brandBg: '#2C3E50',
          },
          {
            id: 'deportv',
            type: 'tv',
            name: 'DeporTV',
            poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/DeporTV_logo_2020.svg/512px-DeporTV_logo_2020.svg.png',
            genres: ['Deportes'],
            description: 'Canal público de deportes argentino en vivo.',
            isLive: true,
            brandBg: '#0E6251',
          },
        ],
      },
      metas: {
        'c5n': {
          id: 'c5n',
          type: 'tv',
          name: 'C5N en Vivo',
          poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/d/d4/C5N_logo_2021.svg/512px-C5N_logo_2021.svg.png',
          genres: ['Noticias'],
          synopsis: 'Señal de noticias líder con cobertura nacional e internacional en vivo.',
          isLive: true,
        },
        'tn': {
          id: 'tn',
          type: 'tv',
          name: 'TN Todo Noticias',
          poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/c/c2/TN_Logo_2016.svg/512px-TN_Logo_2016.svg.png',
          genres: ['Noticias'],
          synopsis: 'Transmisión ininterrumpida de información, móviles en vivo y actualidad.',
          isLive: true,
        },
        'tv-publica': {
          id: 'tv-publica',
          type: 'tv',
          name: 'TV Pública',
          poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/e/e0/Televisi%C3%B3n_P%C3%BAblica_Argentina_%282021%29.svg/512px-Televisi%C3%B3n_P%C3%BAblica_Argentina_%282021%29.svg.png',
          genres: ['Aire', 'Educativo'],
          synopsis: 'Canal 7, la primera televisora de Argentina, transmitiendo eventos de interés nacional.',
          isLive: true,
        },
        'encuentro': {
          id: 'encuentro',
          type: 'tv',
          name: 'Canal Encuentro',
          poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/1/14/Canal_Encuentro_logo_2020.svg/512px-Canal_Encuentro_logo_2020.svg.png',
          genres: ['Educativo'],
          synopsis: 'Documentales, historia, arte y ciencia con la máxima calidad audiovisual educativa.',
          isLive: true,
        },
        'deportv': {
          id: 'deportv',
          type: 'tv',
          name: 'DeporTV',
          poster: 'https://upload.wikimedia.org/wikipedia/commons/thumb/9/91/DeporTV_logo_2020.svg/512px-DeporTV_logo_2020.svg.png',
          genres: ['Deportes'],
          synopsis: 'Transmisión deportiva oficial de torneos nacionales, atletismo y ligas formativas.',
          isLive: true,
        },
      },
      streams: {
        'c5n': [
          { title: 'YouTube Live Oficial', ytId: 'cBmbvU45p1s', isLive: true, quality: '1080p' },
        ],
        'tn': [
          { title: 'YouTube Live Oficial', ytId: 'cb12KmMMDJA', isLive: true, quality: '1080p' },
        ],
        'tv-publica': [
          { title: 'YouTube Live Oficial', ytId: '4-X4L8i23gQ', isLive: true, quality: '1080p' },
        ],
        'encuentro': [
          { title: 'Señal Oficial HLS', url: 'https://stream.encuentro.gob.ar/live/index.m3u8', isLive: true, quality: '720p' },
        ],
        'deportv': [
          { title: 'Señal Oficial HLS', url: 'https://stream.deportv.gob.ar/live/index.m3u8', isLive: true, quality: '720p' },
        ],
      },
    },
  },
  {
    url: 'yomiri://plugin/com.openmanga.archive',
    category: 'manga',
    featured: false,
    manifest: {
      id: 'com.openmanga.archive',
      name: 'Open Comics & Clásicos',
      version: '1.0.0',
      description: 'Colección de cómics clásicos, novelas gráficas y manga de dominio público y licencias Creative Commons.',
      icon: 'https://images.unsplash.com/photo-1544716278-ca5e3f4abd8c?w=128&auto=format&fit=crop&q=80',
      author: 'Comunidad FOSS',
      types: ['manga'],
      resources: ['catalog', 'meta', 'reader'],
      catalogs: [
        {
          type: 'manga',
          id: 'open-classics',
          name: 'Clásicos Abiertos',
        },
      ],
      behaviorHints: {
        defaultSection: 'manga',
        sectionTitle: 'Manga Abierto',
        sectionIcon: 'MenuBook',
        staticData: true,
      },
    },
    staticPayload: {
      catalogs: {
        'manga:open-classics': [
          {
            id: 'pepper-carrot',
            type: 'manga',
            name: 'Pepper & Carrot (CC-BY)',
            poster: 'https://www.peppercarrot.com/0_sources/0/ep01_Potion-of-Flight/low-res/en_Pepper-and-Carrot_by-David-Revoy_E01P00.jpg',
            genres: ['Fantasía', 'Humor'],
            description: 'Famoso webcomic de código abierto creado por David Revoy.',
          },
        ],
      },
      metas: {
        'pepper-carrot': {
          id: 'pepper-carrot',
          type: 'manga',
          name: 'Pepper & Carrot (CC-BY)',
          poster: 'https://www.peppercarrot.com/0_sources/0/ep01_Potion-of-Flight/low-res/en_Pepper-and-Carrot_by-David-Revoy_E01P00.jpg',
          genres: ['Fantasía', 'Humor'],
          synopsis: 'Aventuras cómicas y mágicas de la joven bruja Pepper y su gato Carrot.',
          chapters: [
            { id: 'ep1', title: 'Episodio 1: La poción de vuelo', chapterNumber: 1 },
          ],
        },
      },
      reader: {
        'pepper-carrot:ep1': [
          { url: 'https://www.peppercarrot.com/0_sources/0/ep01_Potion-of-Flight/low-res/en_Pepper-and-Carrot_by-David-Revoy_E01P01.jpg' },
          { url: 'https://www.peppercarrot.com/0_sources/0/ep01_Potion-of-Flight/low-res/en_Pepper-and-Carrot_by-David-Revoy_E01P02.jpg' },
          { url: 'https://www.peppercarrot.com/0_sources/0/ep01_Potion-of-Flight/low-res/en_Pepper-and-Carrot_by-David-Revoy_E01P03.jpg' },
        ],
      },
    },
  },
];
